import { WorklistAttachmentType, WorklistStatus } from 'models/worklist';
import { Group, Flex, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { DrawerActionButton } from 'components/Table/Drawer/DrawerActionButton';
import { BsCheck2, BsArrowsAngleExpand, BsArrowCounterclockwise } from 'react-icons/bs';
import { useDrawerContext } from 'context/hooks';
import { MeasurementDetails } from 'pages/Main/Patients/Details/Overview/HeartRateOverview/MeasurementDetails/MeasurementDetails';
import { useGetWorklistRecord } from 'hooks/api/worklist/useGetWorklistRecord';
import { DocumentIdProps } from 'models/general';
import { useUpdateWorklistRecord } from 'hooks/api/worklist/useUpdateWorklistRecord';
import { containsAttachmentTypes } from 'utils/worklist';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParameters } from 'hooks/useSearchParameters';
import { queryKeys } from 'utils/query';

interface Props {
  title: string;
}

export const WorklistDrawerHeader = ({ documentId, title }: Props & DocumentIdProps) => {
  const { data: document } = useGetWorklistRecord({ documentId });
  const { openDrawer, closeDrawer } = useDrawerContext();
  const { t } = useTranslation();
  const { mutate: updateRecord, isPending: updatingRecord } = useUpdateWorklistRecord();
  const queryClient = useQueryClient();
  const { parameters: filters } = useSearchParameters(queryKeys.worklistOverview);

  const handleUpdateRecord = (status: WorklistStatus) => {
    updateRecord(
      { id: documentId, status },
      {
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKeys.worklistOverview, filters] }),
      },
    );
  };

  const measurementId =
    containsAttachmentTypes({
      item: document,
      types: [WorklistAttachmentType.HeartRate, WorklistAttachmentType.HeartRhythmMeasurement],
    }) && document?.attachments?.measurementId
      ? document.attachments.measurementId
      : undefined;

  const handleOpenMeasurement = (id?: string) => {
    if (!id) return;
    closeDrawer();
    openDrawer({
      content: <MeasurementDetails />,
      detailId: id,
    });
  };

  return (
    <Flex justify={'space-between'}>
      <Title my={'xl'} order={3}>
        {title}
      </Title>
      <Group gap={'xl'}>
        <Group gap={'xs'}>
          {measurementId && (
            <DrawerActionButton
              tooltipLabel={t('open_measurement_label')}
              isOutlineVariant
              onClick={() => handleOpenMeasurement(measurementId)}
              Icon={BsArrowsAngleExpand}
            />
          )}
          {document?.status === WorklistStatus.Unread && (
            <DrawerActionButton
              tooltipLabel={t('acknowledge_label')}
              onClick={() => handleUpdateRecord(WorklistStatus.Read)}
              Icon={BsCheck2}
              loading={updatingRecord}
            />
          )}
          {document?.status === WorklistStatus.Read && (
            <DrawerActionButton
              tooltipLabel={t('unacknowledge_label')}
              onClick={() => handleUpdateRecord(WorklistStatus.Unread)}
              Icon={BsArrowCounterclockwise}
              loading={updatingRecord}
            />
          )}
        </Group>
      </Group>
    </Flex>
  );
};
